/**
 * MyApplicationsCard — v49.8. Settings section "My applications".
 * Shows the user's own MyJob applications with status color-code + delete button.
 * The user can remove any application (pending / accepted / rejected).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Loader2, Trash2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { JOB_L } from './MyJob/i18n';

const MyApplicationsCard = () => {
  const { language } = useLanguage();
  const l = JOB_L[language] || JOB_L.fr;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get('/api/myjob/me/applications');
      setItems(r.data.items || []);
    } catch (_) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!window.confirm(l.confirmDelete || 'Supprimer cette candidature ?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/api/myjob/me/applications/${id}`);
      setItems(prev => prev.filter(a => a.id !== id));
      toast.success(l.sent ? 'OK' : 'OK');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="my-applications-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center"><Briefcase className="w-5 h-5 text-amber-300" /></div>
        <div>
          <div className="text-sm font-semibold">{l.myApps}</div>
          <div className="text-xs text-white/50">MyJob</div>
        </div>
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-300" /></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-white/40 italic text-center py-3">{l.empty}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map(a => (
            <div key={a.id} className="p-2 rounded-lg bg-white/5 flex items-center justify-between gap-2" data-testid={`my-app-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{a.offer_title}</p>
                <p className="text-[10px] text-white/40">{new Date(a.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${a.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-300' : a.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                {a.status === 'accepted' ? l.accepted : a.status === 'rejected' ? l.rejected : l.pending}
              </span>
              <button
                onClick={() => del(a.id)}
                disabled={deletingId === a.id}
                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300 disabled:opacity-40 shrink-0"
                title={l.delete}
                aria-label={l.delete}
                data-testid={`my-app-delete-${a.id}`}
              >
                {deletingId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyApplicationsCard;
